import { ApplicationRef, DoBootstrap, NgModule, inject, provideAppInitializer } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, Router } from '@angular/router';
import { AppRoutingModule } from './app.routes';
import { AppComponent } from './app.component';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import { DraftService } from './draft.service';
import { provideHttpClient, withInterceptors, withFetch } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';

import { errorInterceptor } from './error.interceptor';
import { authInterceptor } from './auth.interceptor';
import { duplicateRequestInterceptor } from 'src/app/duplicate-request.interceptor';
import { distributedLogInterceptor } from './distributed-log.interceptor';
import { loadingInterceptor } from './loading.interceptor';
import {
  DistributedLogService,
  DISTRIBUTED_LOG_CONFIG,
  type DistributedLogConfig,
} from './services/distributed-log.service';
import { environment } from 'src/environments/environment';

import { content } from 'src/environments/data';
import { FEATURES } from '../optional-components';
import { DynamicRouteService } from './services/dynamic-route.service';
import { TimezoneService } from './services/timezone.service';
import { OfflineBannerComponent } from './offline/offline-banner.component';
import { isHomeColdLoad, scheduleIdleTask } from './shared/utils/schedule-idle';

/**
  * Reads optional `environment.distributedLog` config without forcing a hard
  * dependency; absent config means "linkage on, transport off" (best-effort, no
  * network traffic unless an endpoint is supplied).
  */
function readDistributedLogConfig(): Partial<DistributedLogConfig> | undefined {
  const raw = (environment as { distributedLog?: Partial<DistributedLogConfig> })
       .distributedLog;
  if (!raw) {
    return undefined;
     }
  // Dominant codebase pattern: `environment.BASEURL + <scheme-less *_APIURL>`.
  // The beacon/fetch transport feeds the endpoint raw, so it must be absolute.
  // Prepend only when present, to keep "no endpoint -> transport off".
  return {
       ...raw,
      endpoint: raw.endpoint ? environment.BASEURL + raw.endpoint : raw.endpoint,
       };
}

@NgModule({
  declarations: [],
  imports: [
    BrowserModule,
    AppComponent,
    CommonModule,
    RouterModule,
    AppRoutingModule,
  ],

  providers: [
    DraftService,
    CookieService,
    provideHttpClient(
      withFetch(),
      withInterceptors([
        loadingInterceptor,
        distributedLogInterceptor,
        authInterceptor,
        errorInterceptor,
        duplicateRequestInterceptor
       ])
    ),
    {
      provide: 'FEATURES',
      useValue: FEATURES
    },
    provideAppInitializer(() => {
      const dynamicRoutes = inject(DynamicRouteService);
      if (isHomeColdLoad()) {
        dynamicRoutes.scheduleInitializeWhenIdle();
        return;
      }
      return dynamicRoutes.initializeRoutes();
    }),
     // Settings load on first consumer (SettingsService.ensureLoaded) — not on homepage boot
     {
       provide: DISTRIBUTED_LOG_CONFIG,
       useValue: readDistributedLogConfig(),
     },
     provideAppInitializer(() => {
       inject(DistributedLogService).installGracefulSignals();
     }),
   ],
})
export class AppModule implements DoBootstrap {
  constructor() {
    console.log('App initialized with features:', FEATURES);
  }

  // AppComponent is standalone, so it cannot be listed in NgModule.bootstrap.
  ngDoBootstrap(appRef: ApplicationRef): void {
    appRef.bootstrap(AppComponent);
  }

  webddntitle = content.WEBDDN_TITTLE
}
